import {
    owaAccountStoreInitializationFailed,
    owaConnectedAccountAdded,
} from '../actions/publicActions';
import {
    AccountProviderType,
    OWAConnectedAccountState,
    OWAConnectedAccount,
    addOWAConnectedAccount,
    isConnectedAccount,
} from 'owa-accounts-store';
import { assertNever } from 'owa-assert';
import { now } from 'owa-datetime';
import { allCalendarAccountsAndCacheLoaded } from 'owa-calendar-account-store';
import { OwaConnectedAccountInitSource } from '../schema/OwaConnectedAccountInitSource';
import { trace } from 'owa-trace';
import getOwaUserConfigurationHeadersOperation from '../utils/getOwaUserConfigurationHeadersOperation';
import { isDefaultMailbox, setUserConfiguration } from 'owa-session-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { getConnectedAccountRequestOptions } from 'owa-connected-account-headers';
import { getMailboxRequestOptions, MailboxRequestOptions } from 'owa-request-options-types';
import type { AccountSource } from 'owa-graph-schema';
import { setIsImmutableIdFeatureOnForConnectedAccount } from 'owa-immutable-id-store';

/**
 * Adds the OWAConnectedAccount (which is generated from AccountSource) to the
 * `owa-account-store` and dispatches the `owaConnectedAccountAdded` action
 * when this is complete
 * @param account account to be initialized
 */
export async function initializeOwaConnectedAccountV2(
    accountSource: AccountSource,
    source: OwaConnectedAccountInitSource
): Promise<void> {
    const accountProviderType = getAccountProviderTypeFromAccountMailboxType(
        accountSource.mailbox ? accountSource.mailbox.mailboxType : ''
    );

    // Map the fields from the given AccountSource into a OWAConnectedAccount object.
    const account: OWAConnectedAccount = {
        userIdentity: accountSource.mailbox ? accountSource.mailbox.emailAddress : '',
        accountId: accountSource.id,
        accountUniqueId: accountSource.id,
        anchorMailbox: accountSource.mailbox ? accountSource.mailbox.emailAddress : '',
        token: '', // Not used.
        tokenExpiry: now(), // Not used
        accountProviderType: accountProviderType,
        accountState: OWAConnectedAccountState.Valid, // Not used
    };

    const { userIdentity } = account;
    try {
        addOWAConnectedAccount(account);
        if (!isDefaultMailbox(userIdentity)) {
            const mailboxInfo = getUserMailboxInfo(userIdentity);
            const requestOptions = await getConnectedAccountRequestOptions(
                getMailboxRequestOptions(mailboxInfo)! as MailboxRequestOptions
            );

            if (requestOptions.headers) {
                // $TODO VSO 58329: we need to figure out when to retry this call if it fails.
                const userConfiguration = await getOwaUserConfigurationHeadersOperation(
                    requestOptions
                );
                // $TODO VSO 129017: Review this logic for isImmutableIdFeatureOn.
                if (isConnectedAccount(userIdentity)) {
                    const isImmutableIdFeatureOn =
                        Number(userConfiguration.headers.get('X-IIDs')) == 1;
                    setIsImmutableIdFeatureOnForConnectedAccount(isImmutableIdFeatureOn);
                }
                setUserConfiguration(userConfiguration.userConfig, userIdentity);
            } else {
                owaAccountStoreInitializationFailed(
                    userIdentity,
                    false, // isRetry
                    accountProviderType,
                    'Missing headers'
                );
                allCalendarAccountsAndCacheLoaded(false /* isSuccess */, userIdentity);
                return;
            }
        }
        owaConnectedAccountAdded(userIdentity, accountProviderType, source);
        trace.info(
            '[cal-multiAccounts-sourceId] initializeOwaConnectedAccountV2: added account for ' +
                userIdentity
        );
    } catch (error) {
        const errorMessage = 'Exception' + error;
        owaAccountStoreInitializationFailed(
            userIdentity,
            false, // isRetry
            accountProviderType,
            errorMessage
        );
        allCalendarAccountsAndCacheLoaded(false /* isSuccess */, userIdentity);
        trace.info(
            '[cal-multiAccounts-sourceId] initializeOwaConnectedAccountV2: failed to add account for ' +
                userIdentity
        );
    }
}

/**
 * Mapping from the GQL enum AccountMailboxType (in accountMailbox.graphql)
 * to the AccountProviderType required by OWAConnectedAccount
 *
 * export type AccountMailboxType =
 *   | 'OFFICE365'
 *   | 'OUTLOOKDOTCOM'
 *   | 'GOOGLE'
 *   | 'YAHOO'
 *   | 'ICLOUD'
 *   | 'OTHER';
 *
 * @param mailboxType
 * @returns AccountProviderType
 */
function getAccountProviderTypeFromAccountMailboxType(mailboxType: string): AccountProviderType {
    switch (mailboxType) {
        case 'OFFICE365':
            return 'Office365';
        case 'OUTLOOKDOTCOM':
            return 'Outlook';
        case 'GOOGLE':
            return 'Google';
        default:
            return assertNever(mailboxType as never);
    }
}
